import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { Location } from 'history'
import queryString, { ParsedQuery } from 'query-string'
import Dropdown from 'react-bootstrap/Dropdown'
import { MenuPlaceholder } from 'src/components/Placeholders/MenuPlaceholder'
import isFunction from 'lodash/isFunction'
import { PERMISSIONS } from 'src/config/roleConfig'
import { NONE_ID } from 'src/Utils/globalConstants'
import { useNavigateContext } from '../../../Navigation/NavigateContext'
import { CheckBoxInput } from '../../../Inputs/CheckBox'
import { CountChip } from '../../../Chips/BasicChip'
import { FilterName, FilterParam } from '../FilterBar.types'
import {
  FilterDropdownItem,
  FilterDropdownToggle,
  FilterDropdownToggleContent,
} from '../FilterBar.styles'
import { useFilterLabels } from '../FilterLabelsContext'
import { DropdownItemType, NestedFilterType } from './FilterDropdown.constants'
import {
  DropDownMenuWithNesting,
  FilterOptionContainer,
  FilterDropdownIcon,
  FilterItemsContainer,
} from './FilterDropdown.styles'
import { NestedFilter, NestedFilterChevron } from './FilterDropdown.components'
import { EnsureParent } from './FilterDropdown.types'

type FilterDropdownBaseProps = {
  filterParam: FilterParam
  filterName: FilterName
  filterValues: DropdownItemType[]
  isLoading?: boolean
  hasNested?: boolean
  includeParentFilter?: boolean
  isNested?: boolean
  isSingleSelect?: boolean
  icon?: React.ReactNode
  /**
   * When set, checking a parent option writes ONLY the parent value
   * instead of enumerating every nested child, in order to keep the URL compact.
   */
  parentMeansAll?: boolean
}

const asArray = (value: string | string[] | null | undefined): string[] =>
  Array.isArray(value) ? value : value ? [value] : []

type FilterDropdownConditionalProps =
  | {
      show: boolean
      handleToggle: (nextShow: boolean) => void
    }
  | {
      show?: undefined
      handleToggle?: undefined
    }

type FilterDropdownProps = FilterDropdownBaseProps &
  FilterDropdownConditionalProps

const normalizeBracketedKey = (key: string): string =>
  key.replace(/(\[\])+$/g, '')

const normalizeParsedQuery = (
  query: ParsedQuery<string>,
): ParsedQuery<string> => {
  const normalized: ParsedQuery<string> = {}
  const toArray = (value: string | string[] | null | undefined): string[] => {
    if (value === undefined || value === null) return []
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string')
    }
    return typeof value === 'string' ? [value] : []
  }

  Object.entries(query).forEach(([rawKey, rawValue]) => {
    const key = normalizeBracketedKey(rawKey)
    const mergedValues = [...toArray(normalized[key]), ...toArray(rawValue)]
    const dedupedValues = Array.from(new Set(mergedValues))

    if (dedupedValues.length === 0) {
      return
    }
    normalized[key] =
      dedupedValues.length === 1 ? dedupedValues[0] : dedupedValues
  })

  return normalized
}

export const FilterDropdown = React.memo(
  ({
    filterParam,
    filterName,
    filterValues,
    isLoading,
    show,
    handleToggle,
    hasNested,
    includeParentFilter,
    isNested,
    isSingleSelect = false,
    icon,
    parentMeansAll,
  }: FilterDropdownProps): JSX.Element => {
    const [activeSubmenu, setActiveSubmenu] = useState<string>(NONE_ID)
    const [isOpen, setIsOpen] = useState(false)
    const [internalMultiOpen, setInternalMultiOpen] = useState(false)
    const isMultiExternallyControlled = handleToggle !== undefined
    const location = useLocation()
    const searchParamsFallback = useMemo(() => {
      return queryString.parse(location.search, {
        arrayFormat: 'bracket',
      })
    }, [location])
    const {
      navigateState: {
        parsedQueryParams,
        locationSearchString: contextLocationSearchString,
      },
    } = useNavigateContext()
    const searchParams = useMemo(
      () => normalizeParsedQuery(parsedQueryParams || searchParamsFallback),
      [parsedQueryParams, searchParamsFallback],
    )
    const locationSearchString = contextLocationSearchString ?? location.search

    // For single-select, we need to handle the value differently
    const appliedFilters: Record<string, string[] | string> = useMemo(() => {
      if (isSingleSelect) {
        const appliedFilters: Record<string, string> = {}
        const preprocessed = searchParams[filterParam]
        if (Array.isArray(preprocessed)) {
          // If it's an array, take the first value (shouldn't happen but handle it)
          appliedFilters[filterParam] = preprocessed[0] || ''
        } else {
          appliedFilters[filterParam] = (preprocessed as string) || ''
        }
        return appliedFilters
      } else {
        const appliedFilters: Record<string, string[]> = {}
        const preprocessed = searchParams[filterParam]
        if (Array.isArray(preprocessed)) {
          appliedFilters[filterParam] = preprocessed
        } else {
          appliedFilters[filterParam] = preprocessed ? [preprocessed] : []
        }
        filterValues.forEach((value) => {
          const nestedParam = value.nestedFilter?.filterParam
          if (nestedParam && searchParams[nestedParam]) {
            const nestedValue = searchParams[nestedParam]
            appliedFilters[nestedParam] = Array.isArray(nestedValue)
              ? (nestedValue as string[])
              : [nestedValue as string]
          }
        })
        return appliedFilters
      }
    }, [searchParams, filterParam, filterValues, isSingleSelect])

    const isSmartCheckFilter = filterParam === FilterParam.AI_CONTROL_CHECKS

    // For single-select, get the selected value's label
    const selectedValueLabel = useMemo(() => {
      if (!isSingleSelect) return null
      const selectedValue = appliedFilters[filterParam] as string
      if (!selectedValue) return null
      const selectedItem = filterValues.find(
        (item) => item.value === selectedValue,
      )
      return selectedItem?.label || null
    }, [isSingleSelect, appliedFilters, filterParam, filterValues])

    // Determine if there's a selection
    const appliedFilterCount = useMemo(
      () =>
        Array.from(
          new Set(
            Object.values(appliedFilters)
              .flat()
              .filter((value): value is string => typeof value === 'string'),
          ),
        ).length,
      [appliedFilters],
    )

    const hasSelection = isSingleSelect
      ? !!(appliedFilters[filterParam] as string)
      : appliedFilterCount > 0

    const { registerFilter } = useFilterLabels()

    // Register display name + full value map so SavedFiltersModal can
    // show e.g. "Department: Engineering, IT" for any saved filter.
    useEffect(() => {
      const valueMap: Record<string, string> = {}
      filterValues.forEach((fv) => {
        if (typeof fv.label === 'string') valueMap[fv.value] = fv.label
      })
      if (Object.keys(valueMap).length > 0) {
        registerFilter(filterParam, filterName, valueMap)
      }
    }, [filterValues, filterParam, filterName, registerFilter])

    // Single-select: internal open state. Multi-select: parent-controlled or internal
    // so react-bootstrap always receives an explicit `show` and root-close works reliably.
    const multiSelectShow = isMultiExternallyControlled
      ? show
      : internalMultiOpen
    const dropdownShow = isSingleSelect ? isOpen : multiSelectShow

    const dropdownOnToggle = useCallback(
      (nextShow: boolean) => {
        setActiveSubmenu(NONE_ID)
        if (isSingleSelect) {
          setIsOpen(nextShow)
        } else if (isMultiExternallyControlled) {
          handleToggle(nextShow)
        } else {
          setInternalMultiOpen(nextShow)
        }
      },
      [isSingleSelect, isMultiExternallyControlled, handleToggle],
    )

    return (
      <Dropdown
        autoClose={isSingleSelect ? true : 'outside'}
        show={dropdownShow}
        onToggle={dropdownOnToggle}
      >
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          $isAi={isSmartCheckFilter}
          id={`${filterName}-dropdown`}
          $hasSelection={hasSelection}
          $isNested={isNested}
        >
          <FilterDropdownToggleContent>
            {icon && <FilterDropdownIcon>{icon}</FilterDropdownIcon>}
            {isSingleSelect && selectedValueLabel ? (
              <p>
                {filterName}: {selectedValueLabel}
              </p>
            ) : (
              <>
                <p>{filterName}</p>
                {!isSingleSelect && appliedFilterCount > 0 && (
                  <CountChip ml="xxs">{appliedFilterCount}</CountChip>
                )}
              </>
            )}
          </FilterDropdownToggleContent>
        </Dropdown.Toggle>
        {isLoading ? (
          <Dropdown.Menu rootCloseEvent="mousedown">
            <MenuPlaceholder height={108} />
          </Dropdown.Menu>
        ) : (
          <DropDownMenuWithNesting $hasNested={hasNested}>
            <FilterItemsContainer>
              {isSingleSelect ? (
                <SingleSelectDropdownItems
                  filterParam={filterParam}
                  filterValues={filterValues}
                  location={location}
                  searchParams={searchParams}
                  appliedFilters={appliedFilters as Record<string, string>}
                  onSelect={() => isSingleSelect && setIsOpen(false)}
                />
              ) : (
                <MultiSelectDropdownItems
                  filterParam={filterParam}
                  filterValues={filterValues}
                  location={location}
                  searchParams={searchParams}
                  appliedFilters={appliedFilters as Record<string, string[]>}
                  activeSubmenu={activeSubmenu}
                  setActiveSubmenu={setActiveSubmenu}
                  locationSearchString={locationSearchString}
                  includeParentFilter={includeParentFilter}
                  parentMeansAll={parentMeansAll}
                />
              )}
            </FilterItemsContainer>
          </DropDownMenuWithNesting>
        )}
      </Dropdown>
    )
  },
)

export const SimpleDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  replaceParam,
}: {
  filterParam: FilterParam
  filterValue: DropdownItemType
  appliedFilters: Record<string, string[]>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  replaceParam?: boolean
}): JSX.Element => {
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const newAppliedFilters = new Set(appliedFilters[filterParam]?.map((f) => f))
  const isCustom = isFunction(filterValue.customOnClick)
  const isNestedFilterApplied =
    (filterValue.nestedFilter &&
      appliedFilters[filterValue.nestedFilter.filterParam]?.length > 0) ||
    false
  const isChecked =
    newAppliedFilters.has(filterValue.value) || isNestedFilterApplied
  isChecked
    ? newAppliedFilters.delete(filterValue.value)
    : newAppliedFilters.add(filterValue.value)

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        [filterParam]: replaceParam
          ? [filterValue.value]
          : Array.from(newAppliedFilters),
      },
    },
    { arrayFormat: 'bracket' },
  )

  return (
    <FilterOptionContainer>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        to={href}
        onClick={async (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(href, { replace: true })
          }
        }}
      >
        {filterValue.label}
      </FilterDropdownItem>
    </FilterOptionContainer>
  )
}

export const SimpleDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  replaceParam,
}: {
  filterParam: FilterParam
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string[]>
  replaceParam?: boolean
}): JSX.Element => (
  <>
    {filterValues.map((filterValue) => (
      <SimpleDropdownItemComponent
        key={filterValue.value}
        filterParam={filterParam}
        filterValue={filterValue}
        appliedFilters={appliedFilters}
        baseQuery={searchParams}
        location={location}
        replaceParam={replaceParam}
      />
    ))}
  </>
)

/**
 * Compact "select all" for a parent whose own value is itself a filter: writes
 * only the toggled parent value
 */
const buildParentMeansAllHref = ({
  pathname,
  baseQuery,
  filterParam,
  nestedParam,
  value,
  isChecked,
}: {
  pathname: string
  baseQuery?: queryString.ParsedQuery<string>
  filterParam: FilterParam | string
  nestedParam: FilterParam | string
  value: string
  isChecked: boolean
}): string => {
  const parentValues = new Set(asArray(baseQuery?.[filterParam]))
  if (isChecked) {
    parentValues.delete(value)
  } else {
    parentValues.add(value)
  }
  return queryString.stringifyUrl(
    {
      url: pathname,
      query: {
        ...baseQuery,
        [filterParam]: Array.from(parentValues),
        [nestedParam]: [],
      },
    },
    { arrayFormat: 'bracket' },
  )
}

/**
 * Select/deselect every child, and select/deselect just the parent value.
 *  `includeParentFilter` keeps the parent value alongside the children.
 */
const buildNestedParentHrefs = ({
  pathname,
  baseQuery,
  locationSearchString,
  filterParam,
  nestedFilter,
  value,
  appliedFilters,
  includeParentFilter,
}: {
  pathname: string
  baseQuery?: queryString.ParsedQuery<string>
  locationSearchString: string
  filterParam: FilterParam | string
  nestedFilter: NestedFilterType
  value: string
  appliedFilters: Record<string, string[]>
  includeParentFilter?: boolean
}): {
  selectAllHref: string
  deselectAllHref: string
  selectSelfHref: string
  deselectSelfHref: string
} => {
  const selectAllHref = queryString.stringifyUrl(
    {
      url: pathname,
      query: {
        ...baseQuery,
        [nestedFilter.filterParam]: Array.from(
          nestedFilter.filterValues
            .map((f) => f.value)
            .filter((f) => f !== 'custom') || [],
        ),
        ...(includeParentFilter && {
          [filterParam]: [...(appliedFilters[filterParam] || []), value].filter(
            (v, index, array) => array.indexOf(v) === index,
          ),
        }),
      },
    },
    { arrayFormat: 'bracket' },
  )
  const deselectAllHref = includeParentFilter
    ? queryString.stringifyUrl(
        {
          url: pathname,
          query: {
            ...baseQuery,
            [nestedFilter.filterParam]: [],
            [filterParam]:
              appliedFilters[filterParam]?.filter((f) => f !== value) || [],
          },
        },
        { arrayFormat: 'bracket' },
      )
    : queryString.exclude(
        `${pathname}${locationSearchString}`,
        [nestedFilter.filterParam],
        { arrayFormat: 'bracket' },
      )
  const selectSelfHref = queryString.stringifyUrl(
    {
      url: pathname,
      query: {
        ...baseQuery,
        [filterParam]: [value],
      },
    },
    { arrayFormat: 'bracket' },
  )
  const deselectSelfHref = includeParentFilter
    ? queryString.stringifyUrl(
        {
          url: pathname,
          query: {
            ...baseQuery,
            [filterParam]:
              appliedFilters[filterParam]?.filter((f) => f !== value) || [],
          },
        },
        { arrayFormat: 'bracket' },
      )
    : queryString.exclude(`${pathname}${locationSearchString}`, [filterParam], {
        arrayFormat: 'bracket',
      })
  return { selectAllHref, deselectAllHref, selectSelfHref, deselectSelfHref }
}

export const MultiSelectDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  activeSubmenu,
  setActiveSubmenu,
  locationSearchString,
  includeParentFilter,
  parentMeansAll,
  ensureParent,
  partialFilters,
}: {
  filterParam: FilterParam | string
  filterValue: DropdownItemType
  appliedFilters: Record<string, string[]>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  activeSubmenu: string
  setActiveSubmenu: (value: string) => void
  locationSearchString: string
  includeParentFilter?: boolean
  parentMeansAll?: boolean
  /**
   * Set on nested (child) items under a `parentMeansAll` parent: selecting this
   * child also ensures the parent stays selected while a specific requirement subset is active.
   */
  ensureParent?: EnsureParent
  partialFilters?: Record<string, string[]>
}): JSX.Element => {
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const containerRef = React.useRef<HTMLDivElement>(null)
  const [topOffset, setTopOffset] = useState<number>(0)
  const [portalTarget, setPortalTarget] = useState<Element | null>(null)

  useEffect(() => {
    if (containerRef.current) {
      const menuEl = containerRef.current.closest('.dropdown-menu')
      setPortalTarget(menuEl)
    }
  }, [])

  useEffect(() => {
    const handlePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const menuEl = containerRef.current.closest('.dropdown-menu')
        if (menuEl) {
          const menuRect = menuEl.getBoundingClientRect()
          let computedTop = Math.max(0, rect.top - menuRect.top)

          const childMenuEl = menuEl.querySelector('.dropdown-menu')
          if (childMenuEl) {
            const childRect = childMenuEl.getBoundingClientRect()
            const maxTop = Math.max(0, menuRect.height - childRect.height)
            computedTop = Math.min(computedTop, maxTop)
          }
          setTopOffset((prev) => (prev === computedTop ? prev : computedTop))
        }
      }
    }

    if (activeSubmenu === filterValue.nestedFilter?.filterParam) {
      handlePosition()
      const rafId = requestAnimationFrame(handlePosition)
      const scrollEl = containerRef.current?.parentElement
      if (scrollEl) {
        scrollEl.addEventListener('scroll', handlePosition, { passive: true })
        return () => {
          cancelAnimationFrame(rafId)
          scrollEl.removeEventListener('scroll', handlePosition)
        }
      }
      return () => cancelAnimationFrame(rafId)
    }
  }, [activeSubmenu, filterValue.nestedFilter])

  const newAppliedFilters = new Set(appliedFilters[filterParam]?.map((f) => f))
  const isCustom = isFunction(filterValue.customOnClick)
  const isNestedFilterApplied =
    (!partialFilters &&
      filterValue.nestedFilter &&
      appliedFilters[filterValue.nestedFilter.filterParam]?.length > 0) ||
    false
  const isChecked =
    newAppliedFilters.has(filterValue.value) || isNestedFilterApplied
  const isIndeterminate =
    !isChecked && !!partialFilters?.[filterParam]?.includes(filterValue.value)
  isChecked
    ? newAppliedFilters.delete(filterValue.value)
    : newAppliedFilters.add(filterValue.value)

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        [filterParam]: Array.from(newAppliedFilters),
        // A nested (child) item under a `parentMeansAll` parent keeps the
        // parent's value present
        ...(ensureParent && {
          [ensureParent.filterParam]: Array.from(
            new Set([
              ...asArray(baseQuery?.[ensureParent.filterParam]),
              ensureParent.value,
            ]),
          ),
        }),
      },
    },
    { arrayFormat: 'bracket' },
  )

  const nestedFilterHasValues =
    filterValue.nestedFilter && !!filterValue.nestedFilter.filterValues?.length

  return (
    <FilterOptionContainer ref={containerRef}>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        to={href}
        onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (filterValue.nestedFilter && nestedFilterHasValues) {
            if (activeSubmenu != filterValue.nestedFilter.filterParam) {
              setActiveSubmenu(filterValue.nestedFilter.filterParam)
            } else {
              setActiveSubmenu(NONE_ID)
            }
            return
          }
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(href, { replace: true })
          }
        }}
      >
        <CheckBoxInput
          id={filterValue.value}
          label={filterValue.label as React.ReactNode}
          checked={isChecked}
          indeterminate={isIndeterminate}
          onClick={(e) => {
            e.stopPropagation()
            if (isCustom) {
              filterValue.customOnClick?.()
              return
            }
          }}
          onChange={async (e) => {
            if (isCustom) {
              e.preventDefault()
              return
            }
            if (parentMeansAll && filterValue.nestedFilter) {
              setActiveSubmenu(NONE_ID)
              navigate(
                buildParentMeansAllHref({
                  pathname: location.pathname,
                  baseQuery,
                  filterParam,
                  nestedParam: filterValue.nestedFilter.filterParam,
                  value: filterValue.value,
                  isChecked,
                }),
                { replace: true },
              )
              return
            }
            if (filterValue.nestedFilter) {
              setActiveSubmenu(NONE_ID)
              const {
                selectAllHref,
                deselectAllHref,
                selectSelfHref,
                deselectSelfHref,
              } = buildNestedParentHrefs({
                pathname: location.pathname,
                baseQuery,
                locationSearchString,
                filterParam,
                nestedFilter: filterValue.nestedFilter,
                value: filterValue.value,
                appliedFilters,
                includeParentFilter,
              })
              if (!isChecked) {
                if (nestedFilterHasValues) {
                  navigate(selectAllHref, { replace: true })
                  setActiveSubmenu(filterValue.nestedFilter.filterParam)
                } else {
                  navigate(selectSelfHref, { replace: true })
                  setActiveSubmenu(NONE_ID)
                }
              } else {
                if (nestedFilterHasValues) {
                  navigate(deselectAllHref, { replace: true })
                } else {
                  navigate(deselectSelfHref, { replace: true })
                }
                setActiveSubmenu(NONE_ID)
              }
              return
            }
            navigate(href, { replace: true })
          }}
          tabIndex={-1}
          requiredPermissions={[PERMISSIONS.READ]}
        />
        {filterValue.nestedFilter && nestedFilterHasValues && (
          <NestedFilterChevron
            isOpen={activeSubmenu == filterValue.nestedFilter?.filterParam}
          />
        )}
      </FilterDropdownItem>
      {!!filterValue.nestedFilter &&
        nestedFilterHasValues &&
        activeSubmenu == filterValue.nestedFilter.filterParam &&
        portalTarget &&
        createPortal(
          <NestedFilter
            filterParam={filterValue.nestedFilter.filterParam}
            filterValues={filterValue.nestedFilter.filterValues}
            hasSearch={filterValue.nestedFilter.hasSearch}
            ensureParent={
              parentMeansAll
                ? { filterParam, value: filterValue.value }
                : undefined
            }
            top={topOffset}
          />,
          portalTarget,
        )}
    </FilterOptionContainer>
  )
}

export const MultiSelectDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  activeSubmenu,
  setActiveSubmenu,
  locationSearchString,
  includeParentFilter,
  parentMeansAll,
  ensureParent,
  partialFilters,
}: {
  filterParam: FilterParam | string
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string[]>
  activeSubmenu: string
  setActiveSubmenu: (value: string) => void
  locationSearchString: string
  includeParentFilter?: boolean
  parentMeansAll?: boolean
  ensureParent?: EnsureParent
  partialFilters?: Record<string, string[]>
}): JSX.Element => (
  <>
    {filterValues.map((filterValue) => (
      <MultiSelectDropdownItemComponent
        key={filterValue.value}
        filterParam={filterParam}
        filterValue={filterValue}
        appliedFilters={appliedFilters}
        baseQuery={searchParams}
        location={location}
        activeSubmenu={activeSubmenu}
        setActiveSubmenu={setActiveSubmenu}
        locationSearchString={locationSearchString}
        includeParentFilter={includeParentFilter}
        parentMeansAll={parentMeansAll}
        ensureParent={ensureParent}
        partialFilters={partialFilters}
      />
    ))}
  </>
)

export const SingleSelectDropdownItemComponent = ({
  filterParam,
  filterValue,
  appliedFilters,
  baseQuery,
  location,
  onSelect,
}: {
  filterParam: FilterParam
  filterValue: DropdownItemType
  appliedFilters: Record<string, string>
  baseQuery?: queryString.ParsedQuery<string>
  location: Location
  onSelect?: () => void
}): JSX.Element => {
  const { navigateState } = useNavigateContext()
  const fallBackNavigate = useNavigate()
  const navigate = navigateState.navigate || fallBackNavigate

  const selectedValue = appliedFilters[filterParam]
  const isSelected = selectedValue === filterValue.value
  const isCustom = isFunction(filterValue.customOnClick)

  // For single-select: if clicking the same item, deselect it (remove from URL)
  // Otherwise, select the new item
  const newValue = isSelected ? undefined : filterValue.value

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...baseQuery,
        ...(newValue ? { [filterParam]: newValue } : {}),
      },
    },
    { skipNull: true },
  )

  // Remove the param if deselecting
  const finalHref = isSelected
    ? queryString.exclude(
        `${location.pathname}${location.search}`,
        [filterParam],
        { arrayFormat: 'bracket' },
      )
    : href

  return (
    <FilterOptionContainer>
      <FilterDropdownItem
        key={filterValue.value}
        replace
        to={finalHref}
        onClick={async (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation()
          e.preventDefault()
          if (isCustom) {
            filterValue.customOnClick?.()
          } else {
            navigate(finalHref, { replace: true })
          }
          onSelect?.()
        }}
      >
        {filterValue.label}
      </FilterDropdownItem>
    </FilterOptionContainer>
  )
}

export const SingleSelectDropdownItems = ({
  filterParam,
  filterValues,
  location,
  searchParams,
  appliedFilters,
  onSelect,
}: {
  filterParam: FilterParam
  filterValues: DropdownItemType[]
  location: Location
  searchParams: ParsedQuery
  appliedFilters: Record<string, string>
  onSelect?: () => void
}): JSX.Element => (
  <>
    {filterValues.map((filterValue, index) => (
      <React.Fragment key={filterValue.value}>
        {filterValue.divider && index > 0 && <Dropdown.Divider />}
        <SingleSelectDropdownItemComponent
          filterParam={filterParam}
          filterValue={filterValue}
          appliedFilters={appliedFilters}
          baseQuery={searchParams}
          location={location}
          onSelect={onSelect}
        />
      </React.Fragment>
    ))}
  </>
)
